<script>
export default {
  name: "ReportContainer",
};
</script>

<script setup>
import { defineProps, defineExpose, toRefs, ref, onMounted } from "vue";
import { watchDebounced } from "@vueuse/core";
import { TableauViz, TableauEventType } from "@tableau/embedding-api";
import { TABLEAU_VIZ_FRAME_HEIGHT, TABLEAU_VIZ_FRAME_WIDTH } from "@/constants";

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  width: {
    type: [String, Number],
    default: TABLEAU_VIZ_FRAME_WIDTH,
  },
  height: {
    type: [String, Number],
    default: TABLEAU_VIZ_FRAME_HEIGHT,
  },
  outerClasses: {
    type: String,
    default: "w-full",
  },
  innerClasses: {
    type: String,
    default: "w-full",
  },
  onMarksSelection: {
    type: Function,
  },
  onFilterChanged: {
    type: Function,
  },
  clearMarksSelection: {
    type: Boolean,
    default: false,
  },
});

const {
  src,
  width,
  height,
  outerClasses,
  innerClasses,
  onMarksSelection,
  onFilterChanged,
  clearMarksSelection,
} = toRefs(props);

const viz = ref(null);
const report = ref(null);

const handleFirstInteractive = async () => {
  if (onMarksSelection.value) {
    viz.value.addEventListener(
      TableauEventType.MarkSelectionChanged,
      onMarksSelection.value
    );
  }

  if (onFilterChanged.value) {
    viz.value.addEventListener(
      TableauEventType.FilterChanged,
      onFilterChanged.value
    );
  }
};

const executeGetFilters = async () => {
  const results =
    (
      await Promise.all(
        viz?.value?.workbook?.activeSheet?.worksheets?.map(
          async (sheet) => await sheet.getFiltersAsync()
        ) || []
      )
    )?.flat() || [];
  return results;
};

const executeGetMarkers = async () => {
  const results =
    (
      await Promise.all(
        viz?.value?.workbook?.activeSheet?.worksheets?.map(
          async (sheet) => await sheet.getSelectedMarksAsync()
        ) || []
      )
    )?.flat() || [];
  return results;
};

const executeClearTableauSelections = async () => {
  if (clearMarksSelection.value) {
    viz?.value?.workbook?.activeSheet?.worksheets?.map(async (sheet) => {
      await sheet.clearSelectedMarksAsync();
    });
  }
};

defineExpose({ executeGetFilters, executeGetMarkers });

watchDebounced(clearMarksSelection, executeClearTableauSelections, {
  immediate: false,
  debounce: 500,
  maxWait: 1000,
});

onMounted(() => {
  if (report.value) {
    viz.value = new TableauViz();
    viz.value.src = src.value;
    viz.value.toolbar = "hidden";
    viz.value.width = width.value;
    viz.value.height = height.value;
    viz.value.hideTabs = true;

    viz.value.addEventListener(
      TableauEventType.FirstInteractive,
      handleFirstInteractive
    );

    report.value.appendChild(viz.value);
  }
});
</script>

<template>
  <div :class="outerClasses">
    <div ref="report" :class="innerClasses"></div>
  </div>
</template>
